/**
 * `sync.studyGroups.group(id).folders.*` — folder CRUD with cascading
 * re-parenting on delete.
 */

import type {
  SGDeckProps,
  SGFolder,
} from '@flashka/shared/sync-engine/schema/group-entities';

import { useShallow } from 'zustand/react/shallow';

import { folderAncestorIds } from '../folder-normalize';
import { useStudyGroupStore } from '../internal/store/study-group-store';
import { uuid } from '../internal/uuid';
import { nowISO, sendSG } from './helpers';
import type { SGScopedGroup } from './types';

export function createFoldersApi(groupId: string): SGScopedGroup['folders'] {
  return {
    use: () =>
      useStudyGroupStore(
        useShallow((s) => Object.values(s.groups[groupId]?.folders ?? {})),
      ),
    add: (input) => {
      const id = input.id ?? uuid();
      const now = nowISO();
      const folder: SGFolder = {
        id,
        name: input.name,
        color: input.color,
        parentFolder: input.parentFolder ?? null,
        updatedAtISO: now,
      };
      useStudyGroupStore.getState().mergeFolders(groupId, [folder]);
      sendSG(groupId, {
        type: 'sg-folders-push',
        sgFolders: [folder],
        updatedAtISO: now,
      });
      return id;
    },
    update: (folderId, patch) => {
      const current = useStudyGroupStore.getState().groups[groupId]?.folders[folderId];
      if (!current) return;
      const now = nowISO();
      const next: SGFolder = { ...current, ...patch, updatedAtISO: now };
      useStudyGroupStore.getState().mergeFolders(groupId, [next]);
      sendSG(groupId, {
        type: 'sg-folders-push',
        sgFolders: [next],
        updatedAtISO: now,
      });
    },
    delete: (folderId) => {
      const current = useStudyGroupStore.getState().groups[groupId]?.folders[folderId];
      if (!current) return;
      const now = nowISO();
      const parentId = current.parentFolder ?? null;
      // Soft-delete the folder locally and re-parent direct children
      // + decks under it to its parent so the tree stays walkable.
      const slice = useStudyGroupStore.getState().groups[groupId];
      const childFolderUpdates: SGFolder[] = Object.values(slice?.folders ?? {})
        .filter((f) => f.parentFolder === folderId && f.id !== folderId)
        .map((f) => ({ ...f, parentFolder: parentId, updatedAtISO: now }));
      const reparentedDecks: SGDeckProps[] = Object.values(slice?.deckProps ?? {})
        .filter((d) => d.folderId === folderId && !d.deletedTimeISO)
        .map((d) => ({ ...d, folderId: parentId, updatedAtISO: now }));
      const tombstone: SGFolder = {
        ...current,
        deletedTimeISO: now,
        updatedAtISO: now,
      };
      useStudyGroupStore
        .getState()
        .mergeFolders(groupId, [tombstone, ...childFolderUpdates]);
      if (reparentedDecks.length > 0) {
        // Optimistic local re-parent so the deleter's UI shows decks
        // under the parent folder immediately. Authoritative broadcast
        // is server-driven (see below).
        useStudyGroupStore.getState().mergeDeckProps(groupId, reparentedDecks);
      }
      sendSG(groupId, {
        type: 'sg-folders-push',
        sgFolders: [tombstone, ...childFolderUpdates],
        updatedAtISO: now,
      });
      // Deck re-parenting is NOT sent here. Per-row `sg-deckProperties-push`
      // from a non-(group-owner | deck-owner) is rejected by the SG auth
      // gate, so a member deleting a folder containing other members'
      // decks would leave those decks orphaned at the tombstoned
      // folderId for everyone else — they'd vanish from the tree.
      // `StudyGroupDo.cascadeReparentDecksFromFolderTombstones` does
      // the re-parent + per-row broadcast server-side instead, so the
      // canonical state lands on every member regardless of role.
    },
    move: (folderId, newParentFolder) => {
      const current = useStudyGroupStore.getState().groups[groupId]?.folders[folderId];
      if (!current) return;
      if (newParentFolder === folderId) return;
      // Nesting a folder inside its own descendant makes a parent cycle, which
      // detaches the whole subtree from the root for every member.
      const folders = Object.values(
        useStudyGroupStore.getState().groups[groupId]?.folders ?? {},
      );
      if (
        newParentFolder &&
        folderAncestorIds(folders, newParentFolder).includes(folderId)
      )
        return;
      const now = nowISO();
      const next: SGFolder = {
        ...current,
        parentFolder: newParentFolder,
        updatedAtISO: now,
      };
      useStudyGroupStore.getState().mergeFolders(groupId, [next]);
      sendSG(groupId, {
        type: 'sg-folders-push',
        sgFolders: [next],
        updatedAtISO: now,
      });
    },
  };
}
